import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;
  _deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    this.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;

      const menuitem = e.target.closest('[ref="menuitem"]');
      if (!menuitem) return;

      // Submenü über das <li> (parentElement) suchen
      const li = menuitem.parentElement;
      const submenu = li?.querySelector('[ref="submenu[]"]');
      if (!submenu) return; // Kein Submenü → normal navigieren

      const isAlreadyActive = menuitem === this.#state.activeItem;
      if (isAlreadyActive) return; // 2. Klick → Navigation läuft durch

      // 1. Klick → Submenü öffnen
      e.preventDefault();
      e.stopPropagation();
      this.#openSubmenu(menuitem, submenu);
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.#deactivate();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #state = {
    activeItem: null,
  };

  get overflowMenu() {
    return (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return (this.closest('header-component'));
  }

  #openSubmenu(menuitem, submenu) {
    this.dispatchEvent(new MegaMenuHoverEvent());
    clearTimeout(this._deactivateTimeout);

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== menuitem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevSubmenu = previouslyActiveItem.parentElement?.querySelector('[ref="submenu[]"]');
      if (prevSubmenu) delete prevSubmenu.dataset.active;
    }

    this.#state.activeItem = menuitem;
    this.ariaExpanded = 'true';
    menuitem.ariaExpanded = 'true';
    submenu.dataset.active = '';

    this.#cleanupMutationObserver();
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => this.#cleanupMutationObserver(), 500);

    const finalHeight = submenu.offsetHeight || 0;
    this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  // Wird noch von on:focus/on:blur genutzt – leer lassen schadet nicht
  activate = (event) => {};

  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    if (event.type === 'pointerleave') return;
    clearTimeout(this._deactivateTimeout);
    this._deactivateTimeout = setTimeout(() => this.#deactivate(), 350);
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = item.parentElement?.querySelector('[ref="submenu[]"]');
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) delete submenu.dataset.active;
  };

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
